import { SimulationRunGroupService } from './../../services/simulation-group.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { PredictorModelState } from 'src/app/state/predictor-model/predictor-model.state';
import { InteractionType } from '../../enums/interaction-type';
import { CanvasService } from '../../services/canvas.service';
import { SimulationService } from '../../services/simulation.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LoopModalComponent } from '../loop-modal/loop-modal.component';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SimulationLoopService } from '../../services/simulation-loop.service';
import {
  SimulationDataState,
  SimulationState,
} from '../../enums/simulation-state';
import { DelayService } from '../../services/delay.service';
import { CanvasLayer } from 'src/app/global/interfaces/canvas-layer';
import { SimulationWarningModalComponent } from '../simulation-warning-modal/simulation-warning-modal.component';
import { ApiTerrainService } from '../../services/api-terrain.service';
import { SimulationTrackerActions } from '../../../state/simulation-tracker/simulation-tracker.actions';
import { SimulationLoopSettings } from '../../interfaces/simulation-loop-settings';
import {
  Decision,
  StayOrLeaveStudyAreaModalComponent,
} from '../stay-or-leave-study-area-modal/stay-or-leave-study-area-modal.component';
import { Router } from '@angular/router';
import {
  FeatureFlagKey,
  FeaturesState,
} from '../../../state/features/features.state';
import { LogoutService } from '../../services/logout.service';

@Component({
  selector: 'app-predictor-button-bar',
  templateUrl: './predictor-button-bar.component.html',
  styleUrls: ['./predictor-button-bar.component.scss'],
})
export class PredictorButtonBarComponent implements OnInit, AfterViewInit {
  ngUnsubscribe: Subject<void> = new Subject<void>();
  @ViewChild('loopButton') loopButton!: ElementRef;
  @Select(PredictorModelState.showCanvas) showCanvas$!: Observable<boolean>;
  @Select(PredictorModelState.terrainRendered)
  terrainRendered$!: Observable<boolean>;
  @Select(PredictorModelState.showDetails) showDetails$!: Observable<boolean>;
  @Select(PredictorModelState.scenarios) scenarios$!: Observable<CanvasLayer[]>;
  @Select(PredictorModelState.interactionType)
  interactionType$!: Observable<InteractionType>;
  interactionType!: InteractionType;
  interactionTypeRef = InteractionType;
  simulationId?: string;
  simulationName?: string; 
  buttonBarWidth: number = 0;
  paintSplitLabel: string = 'Paint';
  SimulationState = SimulationState;
  paintSplitItems: MenuItem[] = [];
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setButtonBarWidth();
    this.cdr.detectChanges();
  }

  private maxInitiationPoints: number;
  private maxLoops: number;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private store: Store,
    public canvasService: CanvasService,
    public simulationService: SimulationService,
    public simulationLoopService: SimulationLoopService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private elRef: ElementRef,
    private delayService: DelayService,
    private apiTerrainService: ApiTerrainService,
    private simulationGroupService: SimulationRunGroupService,
    private logoutService: LogoutService
  ) {
    this.simulationService.onRunCompleted = () => {
      this.updateSimulationComplete();
    };
    this.maxInitiationPoints = this.store.selectSnapshot(
      FeaturesState.selectFeatureNumberByKey(
        FeatureFlagKey.BatchInitiationPointThreshold
      )
    );

    this.maxLoops = this.store.selectSnapshot(
      FeaturesState.selectFeatureNumberByKey(FeatureFlagKey.BatchLoopsThreshold)
    );
  }

  finishing = false;
  ngOnInit() {
    this.simulationService.simulationState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(
          (state: SimulationState) =>
            state === SimulationState.Completed ||
            state === SimulationState.Failed
        )
      )
      .subscribe(() => {
        this.finishing = false;
      });

    this.interactionType$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((interactionType: InteractionType) => {
        this.interactionType = interactionType;
        if (
          this.paintSplitLabel.startsWith('Exit ') &&
          interactionType == InteractionType.None
        ) {
          this.paintSplitLabel = this.paintSplitLabel.split(' ')[1];
        }
      });

    this.store.select(PredictorModelState.selectedSimulationId).subscribe({
      next: (id: string | undefined) => {
        if (id) {
          this.simulationId = id;
        }
      },
    });
    this._createPaintButtons();
    this.addMouseScrollFunctionality();
  }
  private _createPaintButtons(disableErase: boolean = false): void {
    this.paintSplitItems = [
      {
        label: 'Paint',
        icon: 'symbol-brush symbol-outlined',
        command: () => this.activateInteractionType('Paint'),
      },
      {
        label: 'Drop',
        icon: 'symbol-colorize symbol-outlined',
        command: () => this.activateInteractionType('Drop'),
      },
      {
        label: 'Erase',
        icon: 'symbol-ink-eraser symbol-outlined',
        disabled: disableErase,
        command: () => this.activateInteractionType('Erase'),
      },
    ];
  }
  ngAfterViewInit(): void {
    this.setButtonBarWidth();
    this.cdr.detectChanges();
  }

  get simulating(): boolean {
    return (
      this.simulationService.simulationState === SimulationState.Completed ||
      this.simulationService.simulationState === SimulationState.Running ||
      this.simulationService.simulationState === SimulationState.InLoop ||
      this.simulationService.simulationState === SimulationState.Paused
    );
  }

  get processing(): boolean {
    return (
      this.simulationService.simulationState === SimulationState.Running ||
      this.simulationService.simulationState === SimulationState.InLoop ||
      this.simulationService.simulationState === SimulationState.Paused
    );
  }

  addMouseScrollFunctionality() {
    const buttonBar: HTMLElement | null = document.getElementById(
      'predictor-button-bar'
    );
    if (buttonBar) {
      buttonBar.addEventListener('wheel', ($event) => {
        $event.preventDefault();
        buttonBar.scrollLeft += $event.deltaY;
      });
    }
  }

  setButtonBarWidth() {
    this.buttonBarWidth = this.elRef.nativeElement.offsetWidth;
  }

  toggleCurrentSplitAction() {
    switch (this.interactionType) {
      case InteractionType.Paint:
      case InteractionType.Drop:
      case InteractionType.Erase:
        this.paintSplitLabel = InteractionType[this.interactionType];
        this.store.dispatch(
          new PredictorModelActions.SetInteractionType(InteractionType.None)
        );
        this.canvasService.togglePanning(true);
        break;
      case InteractionType.None:
        this.activateInteractionType(this.paintSplitLabel);
        break;
      default:
        break;
    }
  }

  activateInteractionType(paintSplitLabel: string) {
    this.store.dispatch(new PredictorModelActions.SetDetailVisibility(false));
    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(
        InteractionType[paintSplitLabel as keyof typeof InteractionType]
      )
    );
    this.paintSplitLabel = 'Exit ' + paintSplitLabel;
    this.canvasService.togglePanning(false);
  }

  toggleDetails() {
    const showDetails: boolean = this.store.selectSnapshot(
      PredictorModelState.showDetails
    );

    this.canvasService.togglePanning(showDetails);
    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(InteractionType.None)
    );
    this.store.dispatch(
      new PredictorModelActions.SetDetailVisibility(!showDetails)
    );
  }

  eanbleFinish(): boolean {
    if (this.canvasService.agents.length === 0) {
      return false;
    }

    switch (this.simulationService.simulationState) {
      case SimulationState.Running:
      case SimulationState.Paused:
      case SimulationState.Ready:
        return true;
      default:
        return false;
    }
  }

  enableClearButton(): boolean {
    if (
      this.canvasService.agents.length === 0 ||
      this.canvasService.canvasConfigured === false
    ) {
      return false;
    }

    switch (this.simulationService.simulationState) {
      case SimulationState.Running:
      case SimulationState.InLoop:
        return false;
      default:
        return true;
    }
  }

  ngOndestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  simulatedAgents: boolean = false;
  simulationStepped: boolean = false;

  updateSimulationComplete(complete: boolean = true) {
    this.simulatedAgents = complete;
  }

  actionButtonDisabled() {
    return !this.simulationDataReceivedAndPaused();
  }

  simulationDataReceivedAndPaused() {
    return (
      this.simulationService.simulationState === SimulationState.Paused &&
      this.simulationService.simulationDataState ===
        SimulationDataState.Received
    );
  }
  loopDisabled() {
    return (
      this.canvasService.canvasConfigured === false ||
      this.canvasService.agents.length === 0 ||
      this.completedRender() ||
      this.simulationService.simulationState === SimulationState.Paused ||
      this.simulationService.simulationState === SimulationState.Running
    );
  }

  completedRender() {
    return this.simulationService.simulationState === SimulationState.Completed;
  }
  resetDisabled() {
    const canvasNotConfigured: boolean =
      this.canvasService.canvasConfigured === false;
    const noAgents: boolean = this.canvasService.agents.length === 0;
    const middleOfALoop: boolean = this.simulationLoopService.isLooping();
    const noSimulatedAgents: boolean = !this.simulatedAgents;

    if (
      this.completedRender() ||
      this.simulationService.simulationState === SimulationState.Failed ||
      this.simulationService.simulationState === SimulationState.Paused
    )
      return false;

    return (
      canvasNotConfigured ||
      this.actionButtonDisabled() ||
      noAgents ||
      middleOfALoop ||
      noSimulatedAgents
    );
  }

  steppedSimulation() {
    this.simulationStepped = true;
    if (this.simulationService.isLastStep()) this.updateSimulationComplete();
  }

  shouldDisplayGo() {
    return this.simulationService.canBeRun();
  }

  go() {
    if (!this.simulationId) {
      throw new Error('terrain ID is not set');
    }

    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(InteractionType.None)
    );

    switch (this.simulationService.simulationState) {
      case SimulationState.Running:
        this.simulationService.pause();
        this._createPaintButtons(true);
        break;
      case SimulationState.Paused:
        this.simulationService.resume();
        this._createPaintButtons();
        break;
      default:
        {
        const loopSettings: SimulationLoopSettings = {
          numberOfIterations: 1,
          disableLoopVisualizations: false
        };
        this.validateSimulationDetailsAndPerformAction(
          this.simulationId,
          loopSettings,
          () => {
            this.simulationService.run(this.simulationId!);
          }
        );
        
        break;
      }
    }
  }

  nextStep() {
    if (!this.simulationId) {
      throw new Error('terrain ID is not set');
    }

    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(InteractionType.None)
    );

    switch (this.simulationService.simulationState) {
      case SimulationState.Running:
      case SimulationState.Paused:
        this.simulationService.nextStep(this.simulationId);
        break;
      default:
        {
        const loopSettings: SimulationLoopSettings = {
          numberOfIterations: 1,
          disableLoopVisualizations: false,
        };
        this.validateSimulationDetailsAndPerformAction(
          this.simulationId,
          loopSettings,
          () => {
            this.simulationService.nextStep(this.simulationId!);
          },
          false //To hide the in-progress component.
        );
        break;
    }
  }
  }

  finish() {
    if (!this.simulationId) {
      throw new Error('terrain ID is not set');
    }
    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(InteractionType.None)
    );

    switch (this.simulationService.simulationState) {
      case SimulationState.Running:
      default:
        {
        const loopSettings: SimulationLoopSettings = {
          numberOfIterations: 1,
          disableLoopVisualizations: false,
        };
        this.validateSimulationDetailsAndPerformAction(
          this.simulationId,
          loopSettings,
          () => {
            this.finishing = true;
            this.simulationService.finish(this.simulationId!);
          }
        );
        break;
      }
    }
  }

  reset() {
    if (!this.simulationId) {
      throw new Error('terrain ID is not set');
    }
    this.updateSimulationComplete(false);
    this.simulationService.reset(this.simulationId);
    this.simulationLoopService.reset();
    if (this.simulationLoopService.isLooping()) {
      this.simulationLoopService.killLoop(this.simulationId);
    }
  }

  async killLoop() {
    if (!this.simulationId) {
      throw new Error('terrain ID is not set');
    }

    this.simulationLoopService.killLoop(this.simulationId);
    await this.delayService.delay(100);
    this.reset();
  }

  getLoopModalPositionStyle(loopButton: ElementRef): any {
    const rect: any = loopButton.nativeElement.getBoundingClientRect();
    const right: number = window.innerWidth - rect.right - 24;
    const bottom: number = window.innerHeight - rect.top + 40;

    return {
      position: 'absolute',
      bottom: `${bottom}px`,
      right: `${right}px`,
    };
  }

  openLoopModal() {
    const loopModal: DynamicDialogRef = this.dialogService.open(
      LoopModalComponent,
      {
        width: '400px',
        header: 'Iterations',
        dismissableMask: true,
        styleClass: 'bottom-rounded',
        style: this.getLoopModalPositionStyle(this.loopButton),
      }
    );

    loopModal.onClose.pipe(take(1)).subscribe((loopSettings) => {
      if (loopSettings) {
        this.handleLoopModalResponse(loopSettings);
      }
    });

    this.store.dispatch(
      new PredictorModelActions.SetInteractionType(InteractionType.None)
    );
  }

  handleLoopModalResponse(loopSettings: SimulationLoopSettings) {
    if (this.hasMultipleScenarios()) {
      this.showWarningModal(
        'Multiple Scenario Run',
        'when running multiple scenarios',
        loopSettings,
        true
      );
      return;
    }

    if (!this.simulationId) return;

    this.validateSimulationDetailsAndPerformAction(
      this.simulationId,
      loopSettings,
      () => {
        this.loop(loopSettings);
      }
    );
  }

  private validateSimulationDetailsAndPerformAction(
    simulationId: string,
    loopSettings: SimulationLoopSettings,
    action: Function,
    showInProgressModel:boolean=true
  ) {
    this.apiTerrainService
      .getPreSimulationDetails(simulationId)
      .pipe(take(1))
      .subscribe((details) => {
        if (this.exceedsLimits(details, loopSettings)) {
          const issueText: string = this.getIssueText(
            details.initiationPointsCount,
            loopSettings.numberOfIterations
          );
          this.showWarningModal(
            'Visualization Not Available',
            issueText,
            loopSettings
          );
        } else {
          action();
        }
      });
  }

  private hasMultipleScenarios(): boolean {
    const scenarios: CanvasLayer[] = this.store.selectSnapshot(
      PredictorModelState.scenarios
    );
    return scenarios.length > 0;
  }


  private exceedsLimits(
    details: { initiationPointsCount: number },
    loopSettings: { numberOfIterations: number }
  ): boolean {
    return (
      details.initiationPointsCount > this.maxInitiationPoints ||
      loopSettings.numberOfIterations > this.maxLoops
    );
  }

  loop(loopSettings: SimulationLoopSettings) {
    if (this.simulationId == null) return;
    this.simulationLoopService.loop(
      this.simulationId,
      loopSettings.numberOfIterations,
      loopSettings.disableLoopVisualizations,
      () => {
        console.log('loop ended');
      }
    );
  }

  confirmClear(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'This will remove all placed agents. Are you sure that you want to proceed?',
      accept: () => {
        this.store.dispatch(
          new PredictorModelActions.SetInteractionType(InteractionType.None)
        );
        this.canvasService.clearAllAgents();
        this.canvasService.setAddAgentCompleted(false);
        if (this.simulationId != null)
          this.simulationService.reset(this.simulationId);
        this.simulationLoopService.reset();
      },
      reject: () => {
        console.info('Clear agents cancelled');
      },
      acceptIcon: '.',
      rejectIcon: '.',
      rejectButtonStyleClass: 'p-button-text',
      acceptButtonStyleClass: 'p-button-text basin-ml-5',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
    });
  }

  private showWarningModal(
    subheaderText: string,
    issueText: string,
    loopSettings: SimulationLoopSettings,
    scenarioRun: boolean = false
  ) {
    const warningModal: DynamicDialogRef = this.dialogService.open(
      SimulationWarningModalComponent,
      {
        width: '400px',
        header: '',
        modal: true,
        dismissableMask: true,
        styleClass: 'bottom-rounded title-free-header',
        data: {
          subheaderText: subheaderText,
          issueText: issueText,
        },
      }
    );

    warningModal.onClose.pipe(take(1)).subscribe((option) => {
      if (option) {
        // run as batch, trigger the notifications
        if (scenarioRun) {
          this.simulationGroupService.queueSimulationRunGroup(
            this.simulationId!,
            loopSettings,
            option.simulationName
          );
        } else {
          this.triggerBatchSimulation(this.simulationId!, loopSettings,option.simulationName);
        }

        const stayOrLeaveModal: DynamicDialogRef = this.dialogService.open(
          StayOrLeaveStudyAreaModalComponent,
          {
            width: '360px',
            modal: true,
            dismissableMask: true,
            styleClass: 'bottom-rounded title-free-header',
            data: {},
          }
        );

        stayOrLeaveModal.onClose
          .pipe(take(1))
          .subscribe((decision?: Decision) => {
            if (decision === Decision.Leave) {
              this.logoutService.logout();
            }
            else if( decision === Decision.Stay){             
                this.canvasService.clearAllAgents();
                this.canvasService.setAddAgentCompleted(false);
                this.canvasService.clearAllGeoJSON();
            }
          });
      }
    });
  }

  private triggerBatchSimulation(
    simulationId: string,
    loopSettings: SimulationLoopSettings,
     simulationName: string
  ) {
    if (simulationId == null) return;
    this.store.dispatch(
      new SimulationTrackerActions.CreateSimulationBatch(simulationId, {
        iterations: loopSettings.numberOfIterations,
        simulationName : simulationName
      } 
    )
    );
   
  }

  private getIssueText(numInitiationPoints: number, numLoops: number): string {
    const exceedsInitiationPoints: boolean =
      numInitiationPoints > this.maxInitiationPoints;
    const exceedsLoops: boolean = numLoops > this.maxLoops;
    if (exceedsInitiationPoints && exceedsLoops) {
      return (
        'with the current number of initiation points at ' +
        numInitiationPoints.toLocaleString() +
        ' exceeding ' +
        this.maxInitiationPoints.toLocaleString() +
        ' and the number of loops exceeding ' +
        this.maxLoops.toLocaleString()
      );
    } else if (exceedsInitiationPoints) {
      return (
        'with the current number of initiation points at ' +
        numInitiationPoints.toLocaleString() +
        ' exceeding ' +
        this.maxInitiationPoints.toLocaleString()
      );
    } else if (exceedsLoops) {
      return (
        'as you have exceeded the threshold of '+ this.maxLoops.toLocaleString() + ' loops ' 
      );
    }
    return '';
  }
}
